import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Constants } from '@core/app-constants';
import {
  CancelReservedOrderDetailsDto,
  CancelReservedOrderDto,
  CancelReservedOrderItemsAndSubscriptionsRequestDto,
  OrderDetailsRequestDto,
  OrderFinancialTransactionRequestDto,
  OrderItemRowTypeEnumResponseDto,
  OrderPaymentDetailsRequestDto,
  OrderPaymentOperationTypeEnumRequestDto,
  OrderPaymentRequestDto,
  OrderRefundDetailsDto,
  OrderRefundDto,
  OrdersRefundRequestDto,
  PaymentModeResponseDto,
} from '@core/services/apiclient';
import { AbstractPaymentModeFormUtils } from '@shared/components/functional/abstract-payment-mode-form/abstract-payment-mode-form.component';
import { PaymentModeModel } from '@shared/models/payment-mode.model';
import { SalesPaymentModeModel } from '@shared/models/sales-payment-mode.model';
import { clone } from 'underscore';
import { TreeListRowData } from '../models/orders.model';

@Injectable({
  providedIn: 'root',
})
export class PaymentTransactionService {
  createTransactionPayload(
    mode: PaymentModeResponseDto,
    amount: number,
    operationType: OrderPaymentOperationTypeEnumRequestDto,
  ): OrderFinancialTransactionRequestDto {
    return <OrderFinancialTransactionRequestDto>{
      paymentId: -1,
      paymentModeId: mode.id,
      amount: amount || 0,
      paymentOperationType: operationType,
    };
  }

  createPaymentPayload(mode: PaymentModeResponseDto, amount: number): OrderFinancialTransactionRequestDto {
    return this.createTransactionPayload(mode, amount, OrderPaymentOperationTypeEnumRequestDto.Payment);
  }

  createRefundPayload(mode: PaymentModeResponseDto, amount: number): OrderFinancialTransactionRequestDto {
    return this.createTransactionPayload(mode, amount, OrderPaymentOperationTypeEnumRequestDto.Refund);
  }

  createPaymentMode(mode: PaymentModeResponseDto, amount: number): SalesPaymentModeModel {
    return <SalesPaymentModeModel>{
      amount,
      paymentTypeId: mode.id,
      paymentTypeLabel: mode.name,
      paymentMode: mode,
    };
  }

  mapItemToTransaction(
    item: PaymentModeModel,
    payerId: number | null,
    operationType: OrderPaymentOperationTypeEnumRequestDto,
  ): OrderFinancialTransactionRequestDto {
    return <OrderFinancialTransactionRequestDto>{
      payerId: payerId ?? -1,
      paymentId: -1,
      paymentModeId: item?.mode.id,
      amount: item?.amount || 0,
      paymentOperationType: operationType,
    };
  }

  buildPayloadFromForm(
    form: FormGroup,
    payerId: number | null,
    amountToReturn: number,
    operationType: OrderPaymentOperationTypeEnumRequestDto,
  ): OrderFinancialTransactionRequestDto[] {
    const paymentItems = clone(form?.controls?.rows.value);
    const isFreeMode = paymentItems?.every(({ mode }) =>
      Constants.FreeMode.includes(mode?.paymentType?.code),
    );

    if (isFreeMode && operationType === OrderPaymentOperationTypeEnumRequestDto.Refund) {
      return paymentItems.map((paymentItem: PaymentModeModel) =>
        this.mapItemToTransaction(paymentItem, payerId, operationType),
      );
    }

    return (
      AbstractPaymentModeFormUtils.getDirectPaymentResult(paymentItems, amountToReturn)?.map(
        (data: PaymentModeModel) => this.mapItemToTransaction(data, payerId, operationType),
      ) ?? []
    );
  }

  buildPaymentPayloadFromForm(
    form: FormGroup,
    payerId: number | null,
    amountToReturn: number,
  ): OrderFinancialTransactionRequestDto[] {
    return this.buildPayloadFromForm(
      form,
      payerId,
      amountToReturn,
      OrderPaymentOperationTypeEnumRequestDto.Payment,
    );
  }

  buildRefundPayloadFromForm(
    form: FormGroup,
    payerId: number | null,
    amountToReturn: number,
  ): OrderFinancialTransactionRequestDto[] {
    return this.buildPayloadFromForm(
      form,
      payerId,
      amountToReturn,
      OrderPaymentOperationTypeEnumRequestDto.Refund,
    );
  }

  createFreeModeResult(
    paymentModes: PaymentModeResponseDto[],
    totalAmount: number,
  ): {
    availableModes: PaymentModeResponseDto[];
    refundsPayload: OrderFinancialTransactionRequestDto[];
    cancelPaymentModes: SalesPaymentModeModel[];
    isPaymentBreakdown: boolean;
  } {
    const freeMode =
      paymentModes.find((mode) => Constants.FreeMode.includes(mode?.paymentType?.code)) ??
      this.createFreePaymentMode();
    return {
      availableModes: [freeMode],
      refundsPayload: [this.createRefundPayload(freeMode, totalAmount)],
      cancelPaymentModes: [this.createPaymentMode(freeMode, totalAmount)],
      isPaymentBreakdown: false,
    };
  }

  private createFreePaymentMode(): PaymentModeResponseDto {
    return <PaymentModeResponseDto>{
      id: -1,
      name: 'Gratuit',
      code: 'GRATUIT',
      priority: 0,
      paymentType: {
        id: -1,
        name: 'Gratuit',
        code: Constants.FreeMode[0],
        priority: '0',
      },
      authorizePayment: false,
      paymentServerType: 0,
    };
  }

  updatePaymentModesFromForm(form: FormGroup): SalesPaymentModeModel[] {
    const paymentItems = clone(form?.controls?.rows.value);
    return paymentItems.map((paymentItem: PaymentModeModel) => {
      return <SalesPaymentModeModel>{
        amount: paymentItem?.amount || 0,
        paymentTypeId: paymentItem?.mode?.id,
        paymentTypeLabel: paymentItem?.mode?.name,
        paymentMode: paymentItem?.mode,
      };
    });
  }

  applyPayer(
    transactions: OrderFinancialTransactionRequestDto[],
    customer: { id: number } | null,
  ): OrderFinancialTransactionRequestDto[] {
    if (!transactions.length) return transactions;
    const payerId = customer ? customer.id : -1;
    return transactions.map((transaction) => ({
      ...transaction,
      payerId,
    }));
  }

  applyRefundPayer(
    transactions: OrderFinancialTransactionRequestDto[],
    customer: { id: number } | null,
  ): OrderFinancialTransactionRequestDto[] {
    return this.applyPayer(transactions, customer);
  }

  private buildOrdersMap(
    selection: TreeListRowData[],
  ): Map<number, { orderItemIds: number[]; orderSubscriptionIds: number[] }> {
    const ordersMap = new Map<number, { orderItemIds: number[]; orderSubscriptionIds: number[] }>();

    (selection ?? []).forEach((row) => {
      const orderId = row.orderId;
      if (!ordersMap.has(orderId)) {
        ordersMap.set(orderId, { orderItemIds: [], orderSubscriptionIds: [] });
      }

      const orderDetails = ordersMap.get(orderId)!;

      if (
        row.orderItemRowType === OrderItemRowTypeEnumResponseDto.SubscriptionGroup &&
        row.subscriptionOrderId
      ) {
        orderDetails.orderSubscriptionIds.push(row.subscriptionOrderId ?? row.id);
      } else {
        orderDetails.orderItemIds.push(row.id);
      }
    });

    return ordersMap;
  }

  private mapTransactionsToRequest(
    transactions: OrderFinancialTransactionRequestDto[],
    operationType: OrderPaymentOperationTypeEnumRequestDto,
  ): OrderFinancialTransactionRequestDto[] {
    return transactions.map((transaction) => ({
      paymentModeId: transaction.paymentModeId,
      paymentId: transaction.paymentId ?? -1,
      amount: transaction.amount,
      payerId: transaction.payerId,
      paymentOperationType: operationType,
    }));
  }

  public buildPaymentRequest(
    selection: TreeListRowData[],
    financialTransactions: OrderFinancialTransactionRequestDto[],
  ): OrderPaymentRequestDto {
    const ordersMap = this.buildOrdersMap(selection);

    const orders: OrderPaymentDetailsRequestDto[] = Array.from(ordersMap.entries()).map(
      ([orderId, details]) => ({
        orderId,
        orderDetails: {
          orderItems: details.orderItemIds,
          orderSubscriptionIds: details.orderSubscriptionIds,
        } as OrderDetailsRequestDto,
      }),
    );

    const orderFinancialTransactionItems = this.mapTransactionsToRequest(
      financialTransactions,
      OrderPaymentOperationTypeEnumRequestDto.Payment,
    );

    return {
      orders,
      orderFinancialTransactionItems,
    };
  }

  public buildRefundRequest(
    selection: TreeListRowData[],
    transactions: OrderFinancialTransactionRequestDto[],
  ): OrdersRefundRequestDto {
    const ordersMap = this.buildOrdersMap(selection);

    const orders: OrderRefundDto[] = Array.from(ordersMap.entries()).map(([orderId, details]) => ({
      orderId,
      orderDetails: {
        orderItemIds: details.orderItemIds,
        orderSubscriptionIds: details.orderSubscriptionIds,
      } as OrderRefundDetailsDto,
    }));

    const orderFinancialTransactionItems = this.mapTransactionsToRequest(
      transactions,
      OrderPaymentOperationTypeEnumRequestDto.Refund,
    );

    return {
      orders,
      orderFinancialTransactionItems,
    };
  }

  buildCancelReservedRequest(
    selection: TreeListRowData[],
  ): CancelReservedOrderItemsAndSubscriptionsRequestDto {
    const ordersMap = this.buildOrdersMap(selection);

    const orders: CancelReservedOrderDto[] = Array.from(ordersMap.entries()).map(([orderId, details]) => ({
      orderId,
      orderDetails: {
        orderItemIds: details.orderItemIds,
        orderSubscriptionIds: details.orderSubscriptionIds,
      } as CancelReservedOrderDetailsDto,
    }));
    return { orders };
  }
}
